import { MOTE_REST, WATCHER_HOVER } from '../../models/ash-watcher.ts';
import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, keys, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * Vigía de ceniza (models/ash-watcher.ts): a fallen angel that hunts from above. It hangs in the air on slow,
 * heavy beats of its great wings, legs hanging, head bowed to watch the ground; it falls on a hero like a hawk,
 * wings folded, tears the light out of them and beats back up into the air.
 *
 * Conventions: −X on an arm or leg swings it forward (up), −X on a forearm bends the elbow, +Z on the right arm
 * (+X) lifts it out to the side and −Z the left; +X pitches the body, chest and head forward. Wings: on the right
 * one (+X) +Z lifts it, +Y sweeps it back, +X turns its leading edge down; the left mirrors (−Y, −Z); the tip folds
 * back on +Y (right) and droops on −Z. `steal` is the mote of stolen light at its breast, built tiny: scaled by
 * `MOTE` it shows.
 *
 * The dive, the steal and the rise chain: the dive ends low over the hero (`LOW`), the steal is done low and ends
 * low, the rise starts low and ends at the hover. Blows: attack (a rake of the right talons from the air) at 0.3 s;
 * dive: wings folded from 0.15 s, it strikes at 0.55 s and brakes; steal: the talons close on the hero at 0.3 s,
 * the light is torn out at 0.45 s and reaches its breast at 0.9 s; rise: off the ground at 0.15 s, at the hover by 1.0 s.
 */

type B =
  | 'root' | 'body' | 'chest' | 'head' | 'armL' | 'foreL' | 'armR' | 'foreR'
  | 'wingL' | 'wingLtip' | 'wingR' | 'wingRtip' | 'legL' | 'shinL' | 'legR' | 'shinR' | 'steal';
type P = KeyPose<B>;
type Ch = readonly [number, number, number, number?, number?, number?, number?];

const MOTE = 1 / MOTE_REST;
/** How low the dive takes it (model units under the hover: its feet a little over the ground). */
export const LOW = -(WATCHER_HOVER - 0.2);

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const mirror = (c: Ch): Ch => [c[0], -c[1], -c[2], -(c[3] ?? 0), c[4] ?? 0, c[5] ?? 0, c[6] ?? 1];
/** Both wings from the right one's root and tip. */
const wings = (w: Ch, tip: Ch = [0, 0, 0]): P => ({ wingR: w, wingRtip: tip, wingL: mirror(w), wingLtip: mirror(tip) });
/** Folded: the wings swept back and up along its back, the tips crossed behind (diving, landing). */
const FOLD: P = wings([0.1, 1.0, 0.35], [0, 0.8, -0.3]);
/** Spread wide and flat (braking, gliding). */
const SPREAD: P = wings([-0.15, -0.1, 0.05], [0, -0.05, 0.1]);

/** One wingbeat at phase `p` (0 = top of the stroke), `amp` its size: the right wing's root and tip. */
const beat = (b: Bones<B>, p: number, amp = 1): void => {
  const z = keys(p, [[0, 0.6], [0.45, -0.55, inOut], [1, 0.6, inOut]]) * amp;
  const y = keys(p, [[0, 0.05], [0.25, -0.12], [0.5, 0.05], [0.75, 0.3], [1, 0.05]]) * amp;
  const tz = keys(p, [[0, 0.2], [0.25, 0.3], [0.5, -0.3], [0.75, -0.35], [1, 0.2]]) * amp;
  const ty = keys(p, [[0, 0], [0.5, 0.05], [0.7, 0.5], [0.9, 0.3], [1, 0]]) * amp;
  b.wingR.rot(0, y, z);
  b.wingL.rot(0, -y, -z);
  b.wingRtip.rot(0, ty, tz);
  b.wingLtip.rot(0, -ty, -tz);
  // The body heaves on the downstroke.
  b.root.pos(0, 0.07 * amp * Math.sin(TAU * (p - 0.2)), 0);
  b.chest.rot(-0.03 * amp * Math.sin(TAU * (p - 0.05)));
};

/** The legs hang and trail. */
const dangle = (b: Bones<B>, t: number, trail: number): void => {
  b.legL.rot(0.1 + trail * 0.5 + 0.05 * Math.sin(TAU * 0.6 * t), 0, -0.04);
  b.legR.rot(0.16 + trail * 0.5 + 0.05 * Math.sin(TAU * 0.6 * t + 1), 0, 0.04);
  b.shinL.rot(0.25 + trail * 0.2 + 0.06 * Math.sin(TAU * 0.6 * t + 0.6));
  b.shinR.rot(0.35 + trail * 0.2 + 0.06 * Math.sin(TAU * 0.6 * t + 1.6));
};

const hover = (dur: number, name: string, lean: number, amp: number): Clip<B> =>
  clip<B>(name, dur, true, (b, t) => {
    const p = phase(t, dur);
    beat(b, p, amp);
    b.body.rot(lean, 0, 0);
    b.chest.rot(0.06, 0.06 * Math.sin(TAU * phase(t, dur) + 0.4), 0);
    // The head bowed to watch the ground, turning.
    b.head.rot(0.3 - lean * 0.6, 0.25 * Math.sin(TAU * p * (name === 'idle' ? 1 : 0) + 0.5), 0);
    b.armL.rot(0.15 + 0.05 * Math.sin(TAU * (p - 0.3)), 0, -0.12);
    b.armR.rot(0.15 + 0.05 * Math.sin(TAU * (p - 0.3)), 0, 0.12);
    b.foreL.rot(-0.35);
    b.foreR.rot(-0.35);
    dangle(b, t, lean);
  });

/** A rake from the air: the right talons cocked high (0.15 s), torn down across (0.3 s). */
const attack = (): Clip<B> =>
  clip<B>('attack', 1.0, false, (b, t) => {
    const cocked: P = { ...wings([0, 0, 0.7], [0, 0, 0.3]), root: [0, 0, 0, 0, 0.08, -0.05], body: [-0.1, 0.25, 0], chest: [-0.15, 0.35, 0], head: [0.1, -0.2, 0], armR: [-2.4, 0, 0.5], foreR: [-0.6, 0, 0], armL: [0.3, 0, -0.4] };
    const rake: P = { ...wings([0, 0.1, -0.5], [0, 0.1, -0.2]), root: [0, 0, 0, 0, -0.06, 0.12], body: [0.25, -0.25, 0], chest: [0.3, -0.35, 0], head: [0.3, 0.1, 0], armR: [-0.6, 0, -0.2], foreR: [-0.1, 0, 0], armL: [0.4, 0, -0.5] };
    timeline(b, t, [
      [0, {}],
      [0.15, cocked, outQuad],
      [0.3, rake, inCubic],
      [0.5, { ...rake, chest: [0.26, -0.3, 0] }, outCubic],
      [1.0, {}, inOut],
    ], { foreR: 0.03, head: 0.03, wingRtip: 0.04, wingLtip: 0.04 });
    dangle(b, t, 0.2 * envelope(t, 0.1, 0.3, 0.5, 0.9));
  });

const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    const q = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    b.root.pos(0, 0.05 * q, -0.08 * q);
    b.body.rot(-0.2 * q, 0.1 * q, 0);
    b.chest.rot(-0.15 * q, 0, 0.1 * q);
    b.head.rot(-0.3 * q, -0.1 * q, 0.1 * q);
    b.wingR.rot(0, -0.2 * q, 0.6 * q);
    b.wingL.rot(0, 0.2 * q, -0.6 * q);
    b.armL.rot(0.3 * q, 0, -0.3 * q);
    b.armR.rot(0.3 * q, 0, 0.3 * q);
    dangle(b, t, -0.4 * q);
  });

/** Stunned: it sinks, beating out of time, the head lolling. */
const stun = (): Clip<B> =>
  clip<B>('stun', 2.0, true, (b, t) => {
    const P = phase(t, 2.0);
    b.wingR.rot(0, 0, keys(phase(t, 0.67), [[0, 0.5], [0.45, -0.4, inOut], [1, 0.5, inOut]]));
    b.wingL.rot(0, 0, -keys(phase(t + 0.3, 1.0), [[0, 0.4], [0.45, -0.3, inOut], [1, 0.4, inOut]]));
    b.wingRtip.rot(0, 0, -0.2);
    b.wingLtip.rot(0, 0, 0.3);
    b.root.pos(0, -0.25 + 0.06 * Math.sin(TAU * P * 2), 0).rot(0.1 * Math.sin(TAU * P), 0.3 * Math.sin(TAU * P), 0.25 * Math.sin(TAU * P + 0.5));
    b.body.rot(0.25, 0, 0);
    b.head.rot(0.6 + 0.15 * Math.sin(TAU * P + 0.8), 0.3 * Math.cos(TAU * P), 0.2 * Math.sin(TAU * P));
    b.armL.rot(0.3, 0, 0.1);
    b.armR.rot(0.3, 0, -0.1);
    dangle(b, t, 0);
  });

/** The fall (2.2 s): it rears with the wings flung up (0.2 s), they give, it drops (0.9 s on the ground), kneels, falls forward. */
const die = (): Clip<B> =>
  clip<B>('die', 2.2, false, (b, t) => {
    const ground = -WATCHER_HOVER;
    const kneel: P = { legL: [-0.9, 0, -0.1], shinL: [1.6, 0, 0], legR: [-0.2, 0, 0.1], shinR: [1.7, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.2, { ...wings([0, 0.1, 1.0], [0, 0, 0.5]), root: [0, 0, 0, 0, 0.12, 0], body: [-0.3, 0, 0], chest: [-0.2, 0, 0], head: [-0.7, 0, 0], armL: [-1.5, 0, -0.6], armR: [-1.4, 0, 0.6] }, outExpo],
      [0.9, { ...kneel, ...wings([0.2, 0.5, -0.4], [0, 0.4, -0.6]), root: [0, 0, 0, 0, ground + 0.36, 0.05], body: [0.2, 0, 0], chest: [0.3, 0, 0], head: [0.4, 0, 0.2], armL: [-0.4, 0, -0.2], armR: [-0.5, 0, 0.2] }, inQuad],
      [1.05, { ...kneel, ...wings([0.2, 0.6, -0.55], [0, 0.4, -0.7]), root: [0, 0, 0, 0, ground + 0.32, 0.05], body: [0.28, 0, 0], chest: [0.35, 0, 0], head: [0.5, 0, 0.2], armL: [-0.3, 0, -0.2], armR: [-0.4, 0, 0.2] }, outBack],
      [1.6, { ...kneel, ...wings([0.2, 0.6, -0.6], [0, 0.4, -0.75]), root: [0, 0, 0, 0, ground + 0.32, 0.05], body: [0.35, 0.1, 0.05], chest: [0.4, 0.05, 0], head: [0.7, 0.2, 0.3], armL: [-0.2, 0, -0.2], armR: [-0.2, 0, 0.2] }, inOut],
      [2.0, { ...kneel, ...wings([0.3, 0.3, -0.9], [0, 0.2, -0.4]), root: [0, 0, 0, 0, ground + 0.22, 0.2], body: [1.3, 0.1, 0.05], chest: [0.3, 0, 0], head: [0.2, 0.4, 0.4], armL: [-2.0, 0, -0.4], armR: [-2.0, 0, 0.4] }, inCubic],
      [2.2, { ...kneel, ...wings([0.3, 0.3, -0.95], [0, 0.2, -0.45]), root: [0, 0, 0, 0, ground + 0.2, 0.2], body: [1.28, 0.1, 0.05], chest: [0.3, 0, 0], head: [0.25, 0.4, 0.4], armL: [-2.05, 0, -0.4], armR: [-2.05, 0, 0.4] }, outQuad],
    ], { head: 0.05, wingRtip: 0.05, wingLtip: 0.05 });
  }, (t) => {
    const k = Math.max(0, kick(t, 0.9, 3, 8)) + Math.max(0, kick(t, 2.0, 3, 8));
    return [1 + 0.05 * k, 1 - 0.07 * k, 1 + 0.05 * k];
  });

/** Low over the hero, crouched in the air on short beats, the talons forward (the end of the dive, the steal). */
const CROUCH: P = { ...wings([-0.1, -0.15, 0.35], [0, -0.1, 0.2]), root: [0, 0, 0, 0, LOW, 0], body: [0.2, 0, 0], chest: [0.15, 0, 0], head: [0.2, 0, 0], armL: [-0.7, 0, -0.3], foreL: [-0.4, 0, 0], armR: [-0.7, 0, 0.3], foreR: [-0.4, 0, 0], legL: [-0.5, 0, -0.1], shinL: [0.9, 0, 0], legR: [-0.3, 0, 0.1], shinR: [0.8, 0, 0] };

/**
 * Dive (1.0 s): it rears and folds its wings (0.15 s) and drops head first, the talons thrown forward; it strikes
 * at 0.55 s, the wings snapping open to brake, and ends low, crouched over its prey.
 */
const dive = (): Clip<B> =>
  clip<B>('dive', 1.0, false, (b, t) => {
    const tuck: P = { ...FOLD, root: [0, 0, 0, 0, 0.18, -0.1], body: [-0.3, 0, 0], chest: [-0.15, 0, 0], head: [-0.3, 0, 0], armL: [0.4, 0, -0.1], armR: [0.4, 0, 0.1], legL: [0.4, 0, 0], legR: [0.4, 0, 0] };
    const fall: P = { ...FOLD, root: [0.9, 0, 0, 0, LOW * 0.55, 0.45], body: [0.6, 0, 0], chest: [0.2, 0, 0], head: [-0.4, 0, 0], armL: [-1.6, 0, -0.2], foreL: [-0.2, 0, 0], armR: [-1.6, 0, 0.2], foreR: [-0.2, 0, 0], legL: [0.9, 0, 0], legR: [0.9, 0, 0], shinL: [0.3, 0, 0], shinR: [0.3, 0, 0] };
    const strike: P = { ...SPREAD, root: [0.2, 0, 0, 0, LOW - 0.05, 0.4], body: [0.35, 0, 0], chest: [0.3, 0, 0], head: [0.25, 0, 0], armL: [-1.2, 0, -0.35], foreL: [-0.1, 0, 0], armR: [-1.2, 0, 0.35], foreR: [-0.1, 0, 0], legL: [-0.6, 0, -0.1], shinL: [1.0, 0, 0], legR: [-0.4, 0, 0.1], shinR: [0.9, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.15, tuck, outQuad],
      [0.42, fall, inQuad],
      [0.55, strike, outQuad],
      [0.7, { ...strike, ...wings([-0.15, -0.2, 0.7], [0, -0.1, 0.4]) }, outQuad],
      [1.0, CROUCH, inOut],
    ], { head: 0.03, wingRtip: 0.04, wingLtip: 0.04 });
  }, (t) => {
    const s = envelope(t, 0.15, 0.3, 0.45, 0.55);
    const k = Math.max(0, kick(t, 0.55, 3, 8));
    return [1 - 0.08 * s + 0.06 * k, 1 + 0.12 * s - 0.08 * k, 1 - 0.08 * s + 0.06 * k];
  }, 'Picada');

/**
 * Steal (1.4 s), low over the hero: the right talons lunge down and close (0.3 s), it rips back (0.45 s) and a mote
 * of pale light comes away in its claw; it draws it to its breast (0.9 s), where it sinks in with a flare.
 */
const steal = (): Clip<B> =>
  clip<B>('steal', 1.4, false, (b, t) => {
    const lunge: P = { ...CROUCH, ...wings([-0.1, -0.2, 0.6], [0, -0.1, 0.35]), body: [0.4, -0.2, 0], chest: [0.35, -0.3, 0], head: [0.35, -0.1, 0], armR: [-1.5, 0, 0.15], foreR: [-0.05, 0, 0] };
    const rip: P = { ...CROUCH, ...wings([-0.1, 0.1, -0.2], [0, 0.1, -0.2]), root: [0, 0, 0, 0, LOW + 0.1, -0.1], body: [-0.1, 0.25, 0], chest: [-0.2, 0.35, 0], head: [-0.15, 0.15, 0], armR: [-0.9, 0, 0.7], foreR: [-1.6, 0, 0] };
    const hold: P = { ...CROUCH, root: [0, 0, 0, 0, LOW + 0.06, -0.05], body: [0.05, 0, 0], chest: [-0.1, 0, 0], head: [0.25, 0, 0], armR: [-0.7, 0, -0.25], foreR: [-1.9, 0, 0], armL: [-0.6, 0, 0.25], foreL: [-1.7, 0, 0] };
    timeline(b, t, [
      [0, CROUCH],
      [0.2, { ...CROUCH, armR: [-1.9, 0, 0.5], foreR: [-0.7, 0, 0], chest: [-0.05, 0.2, 0] }, outQuad],
      [0.3, lunge, inCubic],
      [0.45, rip, outExpo],
      [0.9, hold, inOut],
      [1.05, { ...hold, chest: [-0.3, 0, 0, 0, 0, 0, 1.06], head: [-0.4, 0, 0], ...wings([0, 0, 0.8], [0, 0, 0.4]) }, outBack],
      [1.4, CROUCH, inOut],
    ], { foreR: 0.03, head: 0.03, wingRtip: 0.04, wingLtip: 0.04 });
    // The mote: torn out at the claw (0.45 s, ahead and low of the breast), drawn in, sunk into the breast by 1.05 s.
    const show = span(t, 0.4, 0.5, outBack) * (1 - span(t, 0.9, 1.05, inQuad));
    const home = span(t, 0.45, 0.9, inOut);
    b.steal.pos(0.28 * (1 - home), -0.45 * (1 - home), 0.6 * (1 - home)).scale(1 + (MOTE - 1) * show * (0.92 + 0.08 * Math.sin(t * 40)));
  }, (t) => {
    const k = Math.max(0, kick(t, 0.9, 3, 7));
    return [1 + 0.03 * k, 1 - 0.03 * k, 1 + 0.03 * k];
  }, 'Robar');

/** Rise (1.1 s): from low over its prey, two great beats carry it back up to its hover. */
const rise = (): Clip<B> =>
  clip<B>('rise', 1.1, false, (b, t) => {
    timeline(b, t, [
      [0, CROUCH],
      [0.25, { ...CROUCH, root: [0, 0, 0, 0, LOW + 0.1, 0], body: [-0.1, 0, 0], chest: [-0.1, 0, 0], legL: [0.3, 0, 0], legR: [0.3, 0, 0], shinL: [0.3, 0, 0], shinR: [0.3, 0, 0] }, outQuad],
      [1.1, {}, inOut],
    ], { head: 0.04 });
    // Two great beats (the timeline eases the root from low up to the hover), the body heaving on each.
    const p = phase(t, 0.55);
    const amp = 1.3 * envelope(t, 0.05, 0.15, 0.85, 1.1);
    const z = keys(p, [[0, 0.7], [0.45, -0.65, inOut], [1, 0.7, inOut]]) * amp;
    const tz = keys(p, [[0, 0.2], [0.5, -0.35], [0.75, -0.4], [1, 0.2]]) * amp;
    b.wingR.rot(0, 0, z);
    b.wingL.rot(0, 0, -z);
    b.wingRtip.rot(0, 0, tz);
    b.wingLtip.rot(0, 0, -tz);
    b.root.pos(0, 0.08 * amp * Math.sin(TAU * (p - 0.2)), 0);
  }, (t) => {
    const k = envelope(t, 0.05, 0.15, 0.25, 0.4);
    return [1 + 0.04 * k, 1 - 0.05 * k, 1 + 0.04 * k];
  }, 'Remontar');

/** Clips by library kind (see ../rigs.ts). */
export const ashWatcherClips = (): Clip[] => [hover(1.6, 'idle', 0, 1), hover(1.2, 'walk', 0.35, 1.15), attack(), hit(), stun(), die(), dive(), rise(), steal()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { ashWatcher: ashWatcherClips };
